import { handleResponse, handleBlobResponse } from "utils/utils";
import { authService } from "services";
import i18n from "i18n";
import {
	isPlanWorkflowDebugEnabled,
	planWorkflowDebug,
	planWorkflowDebugWarn,
} from "utils/planWorkflowDebug";

function fetchWorkflows(query, requestOptions, debugLabel) {
	const url = `${global.BASE_API}workflows${query}`;

	if (isPlanWorkflowDebugEnabled() && debugLabel) {
		planWorkflowDebug("request", debugLabel, url);
	}

	return fetch(url, requestOptions).then(async (response) => {
		const data = await handleResponse(response);
		data.httpStatus = response.status;

		if (isPlanWorkflowDebugEnabled() && debugLabel) {
			const payload = {
				httpStatus: response.status,
				ok: data.ok,
				count: Array.isArray(data.data) ? data.data.length : null,
				errors: data.errors,
			};
			if (!response.ok) {
				planWorkflowDebugWarn("response", debugLabel, payload);
			} else {
				planWorkflowDebug("response", debugLabel, payload);
			}
		}

		return data;
	});
}

const wfTypeByLang = {
	es: { absences: "Ausencias", vacations: "Vacaciones", preferences: "Preferencias" },
	en: { absences: "Absences", vacations: "Vacations", preferences: "Preferences" },
};

export const wfType = new Proxy({}, {
	get(_, key) { return (wfTypeByLang[i18n.language || "es"] || wfTypeByLang.es)[key]; },
});

const wfStatusByLang = {
	es: { approved: "Aprobado", accepted: "Aceptado", pending: "Pendiente", denied: "Denegado", canceled: "Anulado", pendingSecondLevel: "Pendiente aprobación segundo nivel" },
	en: { approved: "Approved", accepted: "Accepted", pending: "Pending", denied: "Denied", canceled: "Canceled", pendingSecondLevel: "Pendiente aprobación segundo nivel" },
};

export const wfStatus = new Proxy({}, {
	get(_, key) { return (wfStatusByLang[i18n.language || "es"] || wfStatusByLang.es)[key]; },
});

export const workflowsService = {
	getAllAbsences,
	getPendingAbsences,
	getManagedAbsences,
	getCounters,
	getUpcomingAbsences,
	getById,
	add,
	addMultiple,
	addWithAttachments,
	update,
	remove,
	getTypes,
	getAttachment,
	updateAttachments,
	removeAttachment,
	downloadImportTemplate,
	prepareImport,
	runImport,
	exportWorkflows,
	getGroupAbsencesForDates,
	getApprovedAbsencesForPlanning,
	getAllPreferences,
	getPendingPreferences,
	getManagedPreferences,
};

// get all absences
function getAllAbsences(options) {
	const requestOptions = {
		method: "GET",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
		},
	};

	const { employee_id, workflow_status } = options;
	let query = `?workflow_type=${wfType.absences}&sort=-requested&page_size=1000`; // TEMPORAL

	if (workflow_status !== undefined)
		query += "&workflow_status=" + workflow_status;

	if (employee_id !== undefined) query += "&employee_id=" + employee_id;

	return fetch(`${global.BASE_API}workflows${query}`, requestOptions).then(
		handleResponse
	);
}

// get pending absences
function getPendingAbsences(options) {
	const requestOptions = {
		method: "GET",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
		},
	};

	let query = `?workflow_type=${wfType.absences}&workflow_status=${wfStatus.pending}&sort=-requested`;

	if (options) {
		const { employee_id, start_from, start_to, page, page_size } = options;

		if (employee_id && employee_id > 0) query += "&employee_id=" + employee_id;

		if (start_from) query += "&start_from=" + start_from;

		if (start_to) query += "&start_to=" + start_to;

		if (page !== undefined) query += "&page=" + page;

		if (page_size) query += "&page_size=" + page_size;
		else query += "&page_size=20";

		if (options.include_all_company !== undefined)
			query += "&include_all_company=" + options.include_all_company;
	} else {
		query += "&page_size=20";
	}

	return fetch(`${global.BASE_API}workflows${query}`, requestOptions).then(
		handleResponse
	);
}

// get managed absences (Approved & denied)
function getManagedAbsences(options) {
	const requestOptions = {
		method: "GET",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
		},
	};

	let query = `?workflow_type=${wfType.absences}&sort=-requested`;

	if (options) {
		const {
			employee_id,
			start_from,
			start_to,
			page,
			page_size,
			workflow_status,
			shift_id,
			answerer_id,
			debugLabel,
		} = options;

		query +=
			"&workflow_status=" +
			(workflow_status ||
				`${wfStatus.approved},${wfStatus.denied},${wfStatus.canceled}`);

		if (employee_id && employee_id > 0) query += "&employee_id=" + employee_id;

		if (shift_id && shift_id > 0) query += "&shift_id=" + shift_id;

		if (answerer_id && answerer_id > 0)
			query += "&answerer_id=" + answerer_id;

		if (start_from) query += "&start_from=" + start_from;

		if (start_to) query += "&start_to=" + start_to;

		if (page !== undefined) query += "&page=" + page;

		if (page_size) query += "&page_size=" + page_size;
		else query += "&page_size=20";

		if (options.include_all_company !== undefined)
			query += "&include_all_company=" + options.include_all_company;

		if (debugLabel) {
			return fetchWorkflows(query, requestOptions, debugLabel);
		}
	} else {
		query += `&workflow_status=${wfStatus.approved},${wfStatus.denied},${wfStatus.canceled}&page_size=20`;
	}

	return fetch(`${global.BASE_API}workflows${query}`, requestOptions).then(
		handleResponse
	);
}

// get workflow counters (currently total pending absences)
function getCounters() {
	const requestOptions = {
		method: "GET",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
		},
	};

	let query = ""; // in the future ¿?

	return fetch(
		`${global.BASE_API}workflows/counters${query}`,
		requestOptions
	).then(handleResponse);
}

// get upcoming absences
function getUpcomingAbsences(options) {
	const requestOptions = {
		method: "GET",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
		},
	};

	const { absence_in_days, start_from, start_to } = options;

	let query = `?workflow_status=${wfStatus.approved}&workflow_type=${wfType.absences}&sort=+start&page_size=1000`; // TEMPORAL

	if (absence_in_days) query += "&absence_in_days=" + absence_in_days;

	if (start_from) query += "&start_from=" + start_from;

	if (start_to) query += "&start_to=" + start_to;

	return fetch(`${global.BASE_API}workflows${query}`, requestOptions).then(
		handleResponse
	);
}

// get one workflow by id
function getById(id) {
	const requestOptions = {
		method: "GET",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
		},
	};

	return fetch(`${global.BASE_API}workflows/${id}`, requestOptions).then(
		handleResponse
	);
}

// add new workflow
function add(workflow) {
	const requestOptions = {
		method: "POST",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
			"Content-Type": "application/json",
		},
		body: JSON.stringify(workflow),
	};

	return fetch(`${global.BASE_API}workflows`, requestOptions).then(
		handleResponse
	);
}

// add multiple workflows in a single request
function addMultiple(workflows) {
	const requestOptions = {
		method: "POST",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
			"Content-Type": "application/json",
			"X-Ninja-Multiple": true,
		},
		body: JSON.stringify(workflows),
	};

	return fetch(`${global.BASE_API}workflows`, requestOptions).then(
		handleResponse
	);
}

function addWithAttachments(formData) {
	const requestOptions = {
		method: "POST",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
		},
		body: formData,
	};

	return fetch(`${global.BASE_API}workflows`, requestOptions).then(
		handleResponse
	);
}

// update workflow
function update(workflow) {
	const requestOptions = {
		method: "PUT",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
			"Content-Type": "application/json",
		},
		body: JSON.stringify(workflow),
	};

	return fetch(
		`${global.BASE_API}workflows/${workflow.id}`,
		requestOptions
	).then(handleResponse);
}

// remove workflow
function remove(id) {
	const requestOptions = {
		method: "DELETE",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
		},
	};

	return fetch(`${global.BASE_API}workflows/${id}`, requestOptions).then(
		handleResponse
	);
}

// get workflow types
function getTypes() {
	const requestOptions = {
		method: "GET",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
		},
	};

	return fetch(`${global.BASE_API}workflows/types`, requestOptions).then(
		handleResponse
	);
}

// attachments

// get one attachment by uuid
function getAttachment(workflowId, attachmentId) {
	const requestOptions = {
		method: "GET",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
		},
	};

	return fetch(
		`${global.BASE_API}workflows/${workflowId}/attachments/${attachmentId}`,
		requestOptions
	).then(handleBlobResponse);
}

// update workflow with attachments
function updateAttachments(id, formData) {
	const requestOptions = {
		method: "POST",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
		},
		body: formData,
	};

	return fetch(
		`${global.BASE_API}workflows/${id}/attachments`,
		requestOptions
	).then(handleResponse);
}

// delete attachment from workflow
function removeAttachment(workflowId, attachmentId) {
	const requestOptions = {
		method: "DELETE",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
		},
	};

	return fetch(
		`${global.BASE_API}workflows/${workflowId}/attachments/${attachmentId}`,
		requestOptions
	).then(handleResponse);
}

/**********************************************************************
 ****** IMPORT PLANS
 ***********************************************************************/

function downloadImportTemplate() {
	const requestOptions = {
		method: "GET",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
			"Content-Type": "application/json",
		},
	};

	return fetch(
		`${global.BASE_API}workflows/imports/template`,
		requestOptions
	).then(handleBlobResponse);
}

function prepareImport(formData) {
	const requestOptions = {
		method: "POST",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
		},
		body: formData,
	};

	return fetch(`${global.BASE_API}workflows/imports`, requestOptions).then(
		handleResponse
	);
}

function runImport(token) {
	const requestOptions = {
		method: "POST",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
		},
	};

	return fetch(
		`${global.BASE_API}workflows/imports/${token}`,
		requestOptions
	).then(handleResponse);
}

// exportar ausencias
function exportWorkflows(options) {
	const requestOptions = {
		method: "GET",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
		},
	};

	const {
		start_from,
		start_to,
		employee_id,
		workflow_status,
		shift_id,
		answerer_id,
		sort,
	} = options;

	let query = `?page_size=1000&workflow_type=${wfType.absences}`;

	if (sort !== undefined) query += "&sort=" + sort;
	else query += "&sort=-requested";

	if (start_from !== undefined) query += "&start_from=" + start_from;

	if (start_to !== undefined) query += "&start_to=" + start_to;

	if (workflow_status !== undefined)
		query += "&workflow_status=" + workflow_status;

	if (employee_id !== undefined && employee_id > 0)
		query += "&employee_id=" + employee_id;

	if (shift_id !== undefined && shift_id > 0)
		query += "&shift_id=" + shift_id;

	if (answerer_id !== undefined && answerer_id > 0)
		query += "&answerer_id=" + answerer_id;

	if (options.include_all_company !== undefined)
		query += "&include_all_company=" + options.include_all_company;

	return fetch(
		`${global.BASE_API}workflows/export${query}`,
		requestOptions
	).then(handleResponse);
}

// get group absences for a date range (for overlap detection in the new absence modal)
function getGroupAbsencesForDates({ group_id, start_from, start_to }) {
	const requestOptions = {
		method: "GET",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
		},
	};

	let query = `?workflow_type=${wfType.absences}&workflow_status=${wfStatus.approved},${wfStatus.pending}&sort=+start&page_size=200`;

	/* 
	// HACK: Cálculo dinámico de días para bypassear el RLS del backend
	// Comentado a petición del equipo de backend (2026-02-23)
	let days = 15; // default fallback
	if (start_from && start_to) {
		const start = new Date(start_from);
		const end = new Date(start_to);
		const diffTime = Math.abs(end - start);
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
		days = diffDays + 1; // Mínimo 1 día si es la misma fecha
	}

	// Hack para RLS: el backend restringe la lectura a empleados normales a menos que 
	// se envíe el parámetro 'absence_in_days', igual que hace el Dashboard.
	query += `&absence_in_days=${days}`; 
	*/

	if (start_from) query += "&start_from=" + start_from;
	if (start_to) query += "&start_to=" + start_to;

	return fetch(`${global.BASE_API}workflows${query}`, requestOptions).then(
		handleResponse
	);
}

// approved absences for a group and planning period (background load in Plan)
function getApprovedAbsencesForPlanning({ group_id, start_from, start_to }) {
	const requestOptions = {
		method: "GET",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
		},
	};

	let query = `?workflow_type=${wfType.absences}&workflow_status=${wfStatus.approved}&sort=+start&page_size=1000`;

	if (start_from) query += "&start_from=" + start_from;
	if (start_to) query += "&start_to=" + start_to;
	if (group_id !== undefined && group_id !== null) {
		query += "&group_id=" + group_id;
	}

	return fetchWorkflows(
		query,
		requestOptions,
		"plan:approved+group_id"
	);
}

// preferencias de turno  ***********************************************************************************

// get all preferences
function getAllPreferences(options) {
	const requestOptions = {
		method: "GET",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
		},
	};

	const { employee_id, workflow_status } = options;
	let query = `?workflow_type=${wfType.preferences}&sort=-requested&page_size=1000`; // TEMPORAL

	if (workflow_status !== undefined)
		query += "&workflow_status=" + workflow_status;

	if (employee_id !== undefined) query += "&employee_id=" + employee_id;

	return fetch(`${global.BASE_API}workflows${query}`, requestOptions).then(
		handleResponse
	);
}

// get pending preferences
function getPendingPreferences(options) {
	const requestOptions = {
		method: "GET",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
		},
	};

	let query = `?workflow_type=${wfType.preferences}&workflow_status=${wfStatus.pending}&sort=-requested`;

	if (options) {
		const { employee_id, start_from, start_to, page, page_size } = options;

		if (employee_id && employee_id > 0) query += "&employee_id=" + employee_id;

		if (start_from) query += "&start_from=" + start_from;

		if (start_to) query += "&start_to=" + start_to;

		if (page !== undefined) query += "&page=" + page;

		if (page_size) query += "&page_size=" + page_size;
		else query += "&page_size=20";
	} else {
		query += "&page_size=20";
	}

	return fetch(`${global.BASE_API}workflows${query}`, requestOptions).then(
		handleResponse
	);
}

// get managed preferences (Approved & denied)
function getManagedPreferences(options) {
	const requestOptions = {
		method: "GET",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
		},
	};

	let query = `?workflow_type=${wfType.preferences}&workflow_status=${wfStatus.approved},${wfStatus.denied},${wfStatus.canceled}&sort=-requested`;

	if (options) {
		const { employee_id, start_from, start_to, page, page_size } = options;

		if (employee_id && employee_id > 0) query += "&employee_id=" + employee_id;

		if (start_from) query += "&start_from=" + start_from;

		if (start_to) query += "&start_to=" + start_to;

		if (page !== undefined) query += "&page=" + page;

		if (page_size) query += "&page_size=" + page_size;
		else query += "&page_size=20";
	}

	return fetch(`${global.BASE_API}workflows${query}`, requestOptions).then(
		handleResponse
	);
}
